import { c as _c } from "react/compiler-runtime";
import { cn } from '@newfront-insurance/core-ui/v2';
import type { ReactNode } from 'react';
import { useAttachments } from './attachments-provider';
import { useChatInputContext } from './chat-input-context';
import { useDragHandlersRef } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
interface ChatInputProps {
  children: ReactNode;
  className?: string;
}

/**
 * Root wrapper component for chat input.
 * Provides the form container and drag/drop handlers.
 * Handlers come from MessagePayloadProvider (populated by ChatAttachments if rendered).
 *
 * @example
 * ```tsx
 * <ChatInput>
 *   <ChatInputTextarea />
 *   <ChatInputToolbar>
 *     <ChatAttachments />
 *     <SendButton />
 *   </ChatInputToolbar>
 * </ChatInput>
 * ```
 */
export function ChatInput(t0) {
  const $ = _c(34);
  const {
    children,
    className
  } = t0;
  const context = useChatInputContext();
  const {
    handleSubmit,
    isPublicMode
  } = context;
  const {
    attachments
  } = useAttachments();
  const {
    additionalContext
  } = useChatOverlayContext();
  let t1;
  if ($[0] !== attachments) {
    t1 = attachments.some(_temp);
    $[0] = attachments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasUploadingFiles = t1;
  let t2;
  if ($[2] !== additionalContext || $[3] !== attachments) {
    const successfulAttachmentUuids = attachments.filter(_temp2).map(_temp3);
    const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids) ? additionalContext.attachmentFileUuids.filter(_temp4) : [];
    t2 = successfulAttachmentUuids.length !== contextAttachmentFileUuids.length || successfulAttachmentUuids.some(uuid_0 => !contextAttachmentFileUuids.includes(uuid_0));
    $[2] = additionalContext;
    $[3] = attachments;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasUnsyncedAttachmentContext = t2;
  const handlersRef = useDragHandlersRef();
  const handlers = handlersRef.current;
  const isDragging = handlers.isDragging ?? false;
  let t3;
  if ($[5] !== handlers.handleDragOver) {
    t3 = handlers.handleDragOver ?? _temp5;
    $[5] = handlers.handleDragOver;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleDragOver = t3;
  let t4;
  if ($[7] !== handlers.handleDragLeave) {
    t4 = handlers.handleDragLeave ?? _temp6;
    $[7] = handlers.handleDragLeave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleDragLeave = t4;
  let t5;
  if ($[9] !== handlers.handleDrop) {
    t5 = handlers.handleDrop ?? _temp7;
    $[9] = handlers.handleDrop;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleDrop = t5;
  let t6;
  if ($[11] !== className) {
    t6 = cn("flex-shrink-0 px-2", className);
    $[11] = className;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== handleSubmit || $[14] !== hasUnsyncedAttachmentContext || $[15] !== hasUploadingFiles) {
    t7 = e => {
      e.preventDefault();
      if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
        return;
      }
      handleSubmit(e);
    };
    $[13] = handleSubmit;
    $[14] = hasUnsyncedAttachmentContext;
    $[15] = hasUploadingFiles;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== isDragging || $[18] !== isPublicMode) {
    t8 = cn("group relative w-full rounded-lg p-[2px]", "bg-border/50", "focus-within:bg-lime-blue-gradient", {
      "bg-transparent": isDragging,
      "bg-pink-yellow-gradient focus-within:bg-pink-yellow-gradient": isPublicMode
    });
    $[17] = isDragging;
    $[18] = isPublicMode;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== children) {
    t9 = <div className="flex w-full flex-col gap-0 overflow-hidden rounded-lg bg-white shadow-[0_0_0_1px_white] group-focus-within:shadow-none">{children}</div>;
    $[20] = children;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleDragLeave || $[23] !== handleDragOver || $[24] !== handleDrop || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className={t8} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>{t9}</div>;
    $[22] = handleDragLeave;
    $[23] = handleDragOver;
    $[24] = handleDrop;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t7) {
    t11 = <form className="mx-auto flex w-full max-w-3xl gap-2" onSubmit={t7}>{t10}</form>;
    $[28] = t10;
    $[29] = t7;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t6) {
    t12 = <div className={t6}>{t11}</div>;
    $[31] = t11;
    $[32] = t6;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp7() {}
function _temp6() {}
function _temp5() {}
function _temp4(uuid) {
  return typeof uuid === "string";
}
function _temp3(attachment_1) {
  return attachment_1.fileUuid;
}
function _temp2(attachment_0) {
  return attachment_0.fileUuid && !attachment_0.uploadError;
}
function _temp(attachment) {
  return attachment.isUploading;
}